import type { PromptEditorInstance } from "@storytell/rich-text";
import { createRoot, createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import type { PromptBarContextType } from "~/components/prompt/PromptBar.types";

// createRoot keeps signals alive outside any component tree (survives unmounts).
// On SSR, createRoot is a no-op context so this is safe to call at module scope.
const [activePromptEditorSignal, setEditorSignal] = createRoot(() =>
  createSignal<PromptEditorInstance | undefined>(undefined)
);

export { activePromptEditorSignal };

// Wrapped setter uses functional form to prevent SolidJS from treating a callable
// PromptEditorInstance as an updater function.
export function setActivePromptEditor(editor: PromptEditorInstance | undefined): void {
  setEditorSignal(() => editor);
}

// Active PromptBarContext value -- shared with docked command center.
const [activePromptBarContextSignal, setContextSignal] = createRoot(() =>
  createSignal<PromptBarContextType | undefined>(undefined)
);

export { activePromptBarContextSignal };

export function setActivePromptBarContext(ctx: PromptBarContextType | undefined): void {
  setContextSignal(() => ctx);
}

// Registry of currently-mounted main-content prompt bars (side-panel bars excluded).
// The account-level credit pill renders only for the most recently mounted main bar:
// during a route transition two main bars briefly co-mount and would otherwise stack
// two pills (ENG-6727). A reactive list rather than a last-write-wins slot means an
// unmounting primary re-elects the surviving bar, so the pill never gets stranded on a
// torn-down bar and vanishing from a still-visible one.
const [mainPromptBarStack, setMainPromptBarStack] = createRoot(() => createSignal<symbol[]>([]));

export function registerMainPromptBar(token: symbol): void {
  setMainPromptBarStack((prev) => [...prev, token]);
}

export function unregisterMainPromptBar(token: symbol): void {
  setMainPromptBarStack((prev) => prev.filter((t) => t !== token));
}

export function isPrimaryMainPromptBar(token: symbol): boolean {
  const stack = mainPromptBarStack();
  return stack.length > 0 && stack[stack.length - 1] === token;
}

// --- Command center shortcut election ---
// Lives here, not in CommandPalette.tsx, so the always-mounted shortcut gate can consult it
// without importing the palette module and dragging its chunk into the app shell.

const primaryCommandCenterIds = new Set<number>();
let nextCommandCenterId = 0;
let focusedCommandCenterId: number | null = null;

export function nextCommandCenterInstanceId(): number {
  return ++nextCommandCenterId;
}

export function registerPrimaryCommandCenter(id: number): void {
  primaryCommandCenterIds.add(id);
}

export function unregisterPrimaryCommandCenter(id: number): void {
  primaryCommandCenterIds.delete(id);
  // Leaving a torn-down instance elected strands the shortcut: the next prompt bar isn't the
  // last-focused one, and the global fallback still sees a claimant that no longer exists.
  if (focusedCommandCenterId === id) focusedCommandCenterId = null;
}

export function markCommandCenterFocused(id: number): void {
  focusedCommandCenterId = id;
}

/**
 * True while a prompt bar is on screen to answer the shortcut itself. Reads the editor signal
 * alongside the registry because a chat-to-chat transition briefly co-mounts two prompt bars:
 * the signal survives that swap, where a bare registry count can dip to zero mid-hand-off and
 * let the global fallback claim a keypress on a route that has a composer.
 */
export function hasPrimaryCommandCenter(): boolean {
  return primaryCommandCenterIds.size > 0 || activePromptEditorSignal() !== undefined;
}

export function isElectedCommandCenter(id: number): boolean {
  return (
    focusedCommandCenterId === id ||
    (focusedCommandCenterId === null && primaryCommandCenterIds.size > 0 && id === Math.min(...primaryCommandCenterIds))
  );
}

// --- Command center dock state persistence ---
// Lives here (not in CommandPalette.tsx) so SidePanel.tsx can import it
// without creating a circular dependency through the heavy CommandPalette module.

const COMMAND_CENTER_DOCKED_KEY = "storytell.command-center-docked";

export function loadDockedState(): boolean {
  if (isServer || typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(COMMAND_CENTER_DOCKED_KEY) === "true";
  } catch {
    return false;
  }
}

export function saveDockedState(docked: boolean): void {
  if (isServer || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(COMMAND_CENTER_DOCKED_KEY, docked ? "true" : "false");
  } catch {
    // ignore quota / private mode
  }
}

export function isCommandCenterDocked(): boolean {
  return loadDockedState();
}

/** Clear the docked state (used by SidePanel header unpin button) */
export function clearCommandCenterDocked(): void {
  saveDockedState(false);
}

// --- Pending docked navigation ---
// When a pill click fires before the docked CommandPalette mounts, we buffer
// the intended navigation here so the docked instance can pick it up on mount.

interface PendingDockedNav {
  view: string;
  addToChatSubPanel?: string;
  customizeSubPanel?: string;
  promptLibraryInitialText?: string;
  toolsFocus?: string;
}

let pendingDockedNav: PendingDockedNav | undefined;

export function setPendingDockedNav(nav: PendingDockedNav | undefined): void {
  pendingDockedNav = nav;
}

export function consumePendingDockedNav(): PendingDockedNav | undefined {
  const nav = pendingDockedNav;
  pendingDockedNav = undefined;
  return nav;
}

// The global palette's chunk only loads on the first ⌘K, so the keypress that triggered the
// load has to survive until the component mounts — otherwise it takes two presses to open.

let pendingGlobalCommandPaletteOpen = false;

export function setPendingGlobalCommandPaletteOpen(): void {
  pendingGlobalCommandPaletteOpen = true;
}

export function consumePendingGlobalCommandPaletteOpen(): boolean {
  const pending = pendingGlobalCommandPaletteOpen;
  pendingGlobalCommandPaletteOpen = false;
  return pending;
}
